import React, { useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { CheckCircle, ArrowLeft, Filter } from 'lucide-react';
import { Link } from 'react-router-dom';
import useServices from '../utils/useServices';
import { getServiceColor, getServiceIcon } from '../utils/servicePresentation';

const Services = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.05 });
  const { services } = useServices();
  const [category, setCategory] = useState('الكل');
  const categories = useMemo(() => ['الكل', ...new Set(services.map(s => s.category).filter(Boolean))], [services]);
  const filtered = category === 'الكل' ? services : services.filter(s => s.category === category);

  return (
    <div className="pt-24 pb-12">
      <div className="container-custom mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            خدماتنا <span className="gradient-text">المتكاملة</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            اختاري النشاط المناسب؛ كل خدمة وأيقونتها ومميزاتها يتم تحديثها مباشرة من لوحة الإدارة.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2 mb-12">
          <div className="px-3 py-2 text-gray-500"><Filter size={17} /></div>
          {categories.map(item => (
            <button key={item} type="button" onClick={() => setCategory(item)} className={`px-4 py-2 rounded-xl border text-sm transition-all ${category === item ? 'bg-orvix-primary border-orvix-primary text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:text-white'}`}>
              {item}
            </button>
          ))}
        </div>

        <div className="space-y-8" ref={ref}>
          {filtered.map((service, index) => {
            const Icon = getServiceIcon(service.iconKey);
            const color = getServiceColor(service.colorKey);
            const features = Array.isArray(service.features) ? service.features : [];
            return (
              <div
                key={service._id || index}
                className={`glass p-8 lg:p-10 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="grid lg:grid-cols-12 gap-8 items-center">
                  <div className="lg:col-span-2">
                    <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${color.gradient} flex items-center justify-center`}>
                      <Icon className="w-10 h-10 text-white" />
                    </div>
                    {service.category && <div className={`inline-flex mt-3 px-2.5 py-1 rounded-lg border text-xs ${color.badge}`}>{service.category}</div>}
                  </div>
                  <div className="lg:col-span-7">
                    <h2 className="text-2xl font-bold text-white mb-4">{service.title}</h2>
                    <p className="text-gray-400 leading-relaxed mb-6">{service.description}</p>
                    {!!features.length && (
                      <div className="grid sm:grid-cols-2 gap-3">
                        {features.map((feature, i) => (
                          <div key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                            <CheckCircle size={16} className="text-orvix-green flex-shrink-0" />
                            {feature}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="lg:col-span-3 lg:text-left">
                    <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                      اطلب الخدمة <ArrowLeft size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
          {!filtered.length && <div className="glass p-12 text-center text-gray-400">لا توجد خدمات في هذا التصنيف حاليًا.</div>}
        </div>
      </div>
    </div>
  );
};

export default Services;
